/**
 * Query keys and cache invalidation for the intel-source screens, built from the generated query-key helpers.
 * One place knows which cached reads a change to a source makes stale.
 */
import { useQueryClient } from '@tanstack/react-query';
import {
  getIntelSourceHarvestedQueryKey,
  getIntelSourceQueryKey,
  listIntelSourceFetchRunsQueryKey,
  listIntelSourceMonitorFetchRunsQueryKey,
  listIntelSourceMonitorsQueryKey,
  listIntelSourcesQueryKey,
} from '../../../generated/api/@tanstack/react-query.gen.js';

/** Largest page the registry list asks for; the candidate list uses the same cap. */
export const SOURCE_PAGE_LIMIT = 200;

/** The registry list. */
export const sourcesListQuery = () => ({ query: { limit: SOURCE_PAGE_LIMIT } });

/** Graph-discovered candidates awaiting approval. */
export const candidateSourcesQuery = () => ({
  query: { approvalStatus: 'candidate' as const, limit: SOURCE_PAGE_LIMIT },
});

/** Refreshes the registry (every list variant) and, when given, one source's detail. */
export function useIntelSourceInvalidation() {
  const queryClient = useQueryClient();
  return (sourceId?: number) => {
    // A key without `query` matches every list variant (registry and candidates).
    queryClient.invalidateQueries({ queryKey: listIntelSourcesQueryKey() });
    if (sourceId !== undefined) {
      queryClient.invalidateQueries({ queryKey: getIntelSourceQueryKey({ path: { id: sourceId } }) });
    }
  };
}

/** Refreshes one source's harvested content (every page size). */
export function useHarvestedInvalidation() {
  const queryClient = useQueryClient();
  return (sourceId: number) => {
    queryClient.invalidateQueries({ queryKey: getIntelSourceHarvestedQueryKey({ path: { id: sourceId } }) });
  };
}

/** Refreshes a source's monitor and its fetch runs. */
export function useMonitorInvalidation() {
  const queryClient = useQueryClient();
  return (sourceId: number, workflowInstanceId?: number) => {
    queryClient.invalidateQueries({
      queryKey: listIntelSourceMonitorsQueryKey({ query: { intelSourceId: sourceId } }),
    });
    if (workflowInstanceId !== undefined) {
      queryClient.invalidateQueries({
        queryKey: listIntelSourceMonitorFetchRunsQueryKey({ path: { workflowInstanceId } }),
      });
    }
    queryClient.invalidateQueries({ queryKey: listIntelSourceFetchRunsQueryKey({ path: { id: sourceId } }) });
  };
}
